import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from './api';
import { toast } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

export default function Account() {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedApi();
  const [memberData, setMemberData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');
  const [showDiscountOptions, setShowDiscountOptions] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    const fetchMemberData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const email = user?.email;
        
        if (!email) {
          setError('No email found in your profile');
          setLoading(false);
          return;
        }

        const res = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/by-email?email=${encodeURIComponent(email)}`);
        const contentType = res.headers.get('content-type') || '';
        const body = contentType.includes('application/json') ? await res.json() : await res.text();
        
        if (!res.ok) {
          throw new Error(typeof body === 'string' ? body : JSON.stringify(body));
        }
        
        setMemberData(body);
      } catch (e) {
        setError(e.message || 'Failed to load account information');
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [isAuthenticated, user, authenticatedFetch]);

  // Fetch Stripe publishable key
  useEffect(() => {
    const fetchStripeConfig = async () => {
      if (!isAuthenticated) return;
      
      try {
        const res = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/stripe/config`);
        if (res.ok) {
          const data = await res.json();
          const publishableKey = data.config.publishableKey;
          if (publishableKey) {
            setStripePublishableKey(publishableKey);
            setStripePromise(loadStripe(publishableKey));
          }
        }
      } catch (e) {
        console.error('Error fetching Stripe config:', e);
      }
    };

    fetchStripeConfig();
  }, [isAuthenticated, authenticatedFetch]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading authentication...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ color: '#38b44a', marginBottom: '2rem' }}>My Account</h2>
      
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading account information...</p>
        </div>
      )}
      
      {error && (
        <div style={{ 
          backgroundColor: '#ffe6e6', 
          border: '1px solid #ff9999', 
          color: '#cc0000',
          padding: '1rem', 
          borderRadius: '6px', 
          marginBottom: '2rem' 
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {memberData && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          padding: '2rem', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: '#38b44a', marginBottom: '1rem' }}>Profile Information</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong>Email:</strong> {memberData.email}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: '#38b44a', marginBottom: '1rem' }}>Subscription Status</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong>Status:</strong> 
                <span style={{ 
                  marginLeft: '0.5rem',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  backgroundColor: memberData.status === 'active' ? '#e8f5e8' : 
                                 (memberData.status === 'subscribed' || memberData.status === 'canceling') ? '#fff3cd' : '#f8d7da',
                  color: memberData.status === 'active' ? '#38b44a' : 
                        (memberData.status === 'subscribed' || memberData.status === 'canceling') ? '#856404' : '#721c24'
                }}>
                  {(() => {
                    if (memberData.status === 'canceling' && memberData.expires) {
                      // Format expires date (unix timestamp) to "canceling on January 28th 2026"
                      const expiresDate = new Date(parseInt(memberData.expires) * 1000);
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                      const month = monthNames[expiresDate.getMonth()];
                      const day = expiresDate.getDate();
                      const year = expiresDate.getFullYear();
                      const daySuffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
                      return `canceling on ${month} ${day}${daySuffix} ${year}`;
                    }
                    return memberData.status || 'Unknown';
                  })()}
                </span>
              </div>
              <div>
                <strong>Subscription:</strong> {memberData.subscription ? memberData.subscription.charAt(0).toUpperCase() + memberData.subscription.slice(1) : 'Not specified'}
              </div>
              {memberData.period && (
                <div>
                  <strong>Billing Period:</strong> {memberData.period}
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              marginTop: '1.5rem',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setShowPaymentDialog(true)}
                style={{
                  backgroundColor: '#38b44a',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
              >
                Update Payment Information
              </button>
              <button
                onClick={() => window.location.href = '/plans'}
                style={{
                  backgroundColor: '#38b44a',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
              >
                Compare Plans
              </button>
            </div>
            
            {/* Cancel Subscription - Bottom */}
            {/* Only show cancel link if status is active or subscribed (not canceling) */}
            {(memberData.status === 'active' || memberData.status === 'subscribed') && (
              <div style={{ marginTop: '1rem', textAlign: 'left' }}>
                <span
                  onClick={() => setShowCancelDialog(true)}
                  style={{
                    color: '#dc3545',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'color 0.2s, text-decoration 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.color = '#c82333';
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.color = '#dc3545';
                    e.target.style.textDecoration = 'none';
                  }}
                >
                  Cancel Subscription
                </span>
              </div>
            )}
          </div>

          {memberData.status === 'new' && (
            <div style={{ 
              backgroundColor: '#e8f5e8', 
              border: '1px solid #38b44a', 
              color: '#38b44a',
              padding: '1rem', 
              borderRadius: '6px', 
              textAlign: 'center' 
            }}>
              <p style={{ margin: 0, fontWeight: '600' }}>
                Ready to get started? Choose a subscription plan to unlock all features!
              </p>
              <a 
                href="/plans" 
                style={{
                  display: 'inline-block',
                  backgroundColor: '#38b44a',
                  color: 'white',
                  textDecoration: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginTop: '1rem'
                }}
              >
                View Plans
              </a>
            </div>
          )}
        </div>
      )}
      
      {!loading && !error && !memberData && (
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          color: '#6c757d',
          padding: '2rem', 
          borderRadius: '6px', 
          textAlign: 'center' 
        }}>
          <p>No account information found. Please contact support if this issue persists.</p>
        </div>
      )}

      {/* Cancel Subscription Dialog */}
      {showCancelDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            {!showDiscountOptions ? (
              <>
                <h3 style={{ 
                  color: '#38b44a', 
                  marginBottom: '1rem',
                  fontSize: '1.5rem',
                  textAlign: 'center'
                }}>
                  We hate to see you go
                </h3>
                <p style={{ 
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  Can you tell us why you are leaving?
                </p>
                
                <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Reason for leaving:
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                  required
                >
                  <option value="">Please select a reason...</option>
                  <option value="freelance">I am no longer looking for freelance work</option>
                  <option value="price">Too expensive</option>
                  <option value="value">I do not get enough value for my subscription</option>
                  <option value="time">I am not using Study Hall as much as I thought I would</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Additional feedback:
                </label>
                <textarea
                  value={cancelFeedback}
                  onChange={(e) => setCancelFeedback(e.target.value)}
                  placeholder="Please share any additional thoughts or suggestions..."
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '16px',
                    minHeight: '100px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                justifyContent: 'flex-end' 
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCancelDialog(false);
                    setCancelReason('');
                    setCancelFeedback('');
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  Keep Subscription
                </button>
                <button
                  type="button"
                  disabled={cancellingSubscription}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    setCancellingSubscription(true);
                    try {
                      // Save cancellation reason and details to database
                      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/cancel-feedback`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          email: user?.email,
                          cancel_reason: cancelReason,
                          cancel_details: cancelFeedback
                        })
                      });

                      if (!response.ok) {
                        let errorMessage = 'Failed to save cancellation feedback';
                        try {
                          const errorData = await response.json();
                          errorMessage = errorData.message || errorData.error || errorMessage;
                        } catch (e) {
                          errorMessage = `${response.status} ${response.statusText}`;
                        }
                        throw new Error(errorMessage);
                      }

                      // Check if subscription is annual - if so, just cancel renewal (no discount options)
                      const isAnnual = memberData?.period?.toLowerCase() === 'annual';
                      // Check if user is on Ghost account - no discount options for Ghost users
                      const isGhostAccount = memberData?.isGhostAccount === true;
                      
                      if (isAnnual || isGhostAccount) {
                        // For annual subscriptions or Ghost account users, just cancel the renewal
                        try {
                          const cancelResponse = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/cancel-renewal`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              email: user?.email
                            })
                          });

                          if (!cancelResponse.ok) {
                            let errorMessage = 'Failed to cancel renewal';
                            try {
                              const errorData = await cancelResponse.json();
                              errorMessage = errorData.message || errorData.error || errorMessage;
                            } catch (e) {
                              errorMessage = `${cancelResponse.status} ${cancelResponse.statusText}`;
                            }
                            throw new Error(errorMessage);
                          }

                          toast.success('Your subscription renewal has been cancelled. You\'ll continue to have access until the end of your current billing period.');
                          setShowCancelDialog(false);
                          setCancelReason('');
                          setCancelFeedback('');
                          // Redirect to home page
                          setTimeout(() => navigate('/'), 500);
                        } catch (error) {
                          setCancellingSubscription(false);
                          toast.error(
                            (t) => (
                              <span>
                                There was an error cancelling your renewal. Please contact{' '}
                                <a 
                                  href="mailto:support@studyhall.xyz" 
                                  style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 'bold' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  support@studyhall.xyz
                                </a>
                                {' for assistance.'}
                              </span>
                            ),
                            { duration: 8000 }
                          );
                        }
                      } else {
                        // For monthly subscriptions (non-Ghost accounts), show discount options
                        setCancellingSubscription(false);
                        setShowCancelDialog(false);
                        setShowDiscountOptions(true);
                      }
                    } catch (error) {
                      setCancellingSubscription(false);
                      toast.error(
                        (t) => (
                          <span>
                            There was an error saving your feedback. Please contact{' '}
                            <a 
                              href="mailto:support@studyhall.xyz" 
                              style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 'bold' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              support@studyhall.xyz
                            </a>
                            {' for assistance.'}
                          </span>
                        ),
                        { duration: 8000 }
                      );
                    }
                  }}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: cancellingSubscription ? 'not-allowed' : 'pointer',
                    opacity: cancellingSubscription ? 0.6 : 1,
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (!cancellingSubscription) {
                      e.target.style.backgroundColor = '#c82333';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!cancellingSubscription) {
                      e.target.style.backgroundColor = '#dc3545';
                    }
                  }}
                >
                  {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
                </button>
              </div>
            </form>
              </>
            ) : (
              <DiscountOptions
                cancelReason={cancelReason}
                selectedDiscount={selectedDiscount}
                setSelectedDiscount={setSelectedDiscount}
                applyingDiscount={applyingDiscount}
                cancellingSubscription={cancellingSubscription}
                navigate={navigate}
                onApplyDiscount={async () => {
                  if (!selectedDiscount) {
                    toast.error('Please select a discount option');
                    return;
                  }
                  
                  try {
                    setApplyingDiscount(true);
                    const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/apply-discount`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        email: user?.email,
                        discount: selectedDiscount
                      })
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.message || 'Failed to apply discount');
                    }

                    // Show toast first
                    toast.success('Discount has been applied');
                    // Close dialog and clear state
                    setShowCancelDialog(false);
                    setShowDiscountOptions(false);
                    setCancelReason('');
                    setCancelFeedback('');
                    setSelectedDiscount(null);
                    // Redirect to homepage
                    navigate('/');
                  } catch (error) {
                    toast.error(error.message || 'There was an error applying the discount. Please try again or contact support.');
                  } finally {
                    setApplyingDiscount(false);
                  }
                }}
                onProceedWithCancellation={async () => {
                  try {
                    setCancellingSubscription(true);
                    const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/cancel-subscription`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        email: user?.email
                      })
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.message || 'Failed to cancel subscription');
                    }

                    toast.success('Your subscription has been cancelled. We\'re sorry to see you go!');
                    setShowCancelDialog(false);
                    setShowDiscountOptions(false);
                    setCancelReason('');
                    setCancelFeedback('');
                    setSelectedDiscount(null);
                    // Redirect to home page
                    setTimeout(() => navigate('/'), 500);
                  } catch (error) {
                    // Show error with support email link
                    const errorMessage = error.message || 'Failed to cancel subscription';
                    toast.error(
                      (t) => (
                        <span>
                          {errorMessage}. Please contact{' '}
                          <a 
                            href="mailto:support@studyhall.xyz" 
                            style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 'bold' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            support@studyhall.xyz
                          </a>
                          {' for assistance.'}
                        </span>
                      ),
                      { 
                        duration: 8000
                      }
                    );
                  } finally {
                    setCancellingSubscription(false);
                  }
                }}
                onKeepSubscription={() => {
                  setShowCancelDialog(false);
                  setShowDiscountOptions(false);
                  setCancelReason('');
                  setCancelFeedback('');
                  setSelectedDiscount(null);
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Payment Update Dialog */}
      {showPaymentDialog && stripePromise && (
        <PaymentUpdateDialog
          stripePromise={stripePromise}
          authenticatedFetch={authenticatedFetch}
          onClose={() => setShowPaymentDialog(false)}
          onSuccess={() => {
            setShowPaymentDialog(false);
            toast.success('Payment method updated successfully');
            // Refresh member data
            const fetchMemberData = async () => {
              try {
                const email = user?.email;
                if (!email) return;
                const res = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/by-email?email=${encodeURIComponent(email)}`);
                if (res.ok) {
                  const body = await res.json();
                  setMemberData(body);
                }
              } catch (e) {
                console.error('Error refreshing member data:', e);
              }
            };
            fetchMemberData();
          }}
        />
      )}
    </div>
  );
}

// Payment Update Dialog Component
function PaymentUpdateDialog({ stripePromise, authenticatedFetch, onClose, onSuccess }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const createSetupIntent = async () => {
      try {
        setLoading(true);
        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/payment-method/create-setup-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to create setup intent');
        }

        const data = await response.json();
        setClientSecret(data.clientSecret);
      } catch (err) {
        // Always show generic error message
        setError('Unable to update payment information. Please contact support@studyhall.xyz for assistance.');
      } finally {
        setLoading(false);
      }
    };

    createSetupIntent();
  }, [authenticatedFetch]);

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center'
        }}>
          <p>Loading payment form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
        }}>
          <h3 style={{ color: '#dc3545', marginBottom: '1rem' }}>Unable to Update Payment Information</h3>
          <p style={{ marginBottom: '1.5rem', color: '#666' }}>
            Please contact{' '}
            <a 
              href="mailto:support@studyhall.xyz" 
              style={{ color: '#38b44a', textDecoration: 'underline', fontWeight: 'bold' }}
            >
              support@studyhall.xyz
            </a>
            {' for assistance.'}
          </p>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
      }}>
        <h3 style={{ 
          color: '#38b44a', 
          marginBottom: '1rem',
          fontSize: '1.5rem',
          textAlign: 'center'
        }}>
          Update Payment Information
        </h3>
        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentFormContent
              clientSecret={clientSecret}
              authenticatedFetch={authenticatedFetch}
              onSuccess={onSuccess}
              onClose={onClose}
              processing={processing}
              setProcessing={setProcessing}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}

// Payment Form Content Component
function PaymentFormContent({ clientSecret, authenticatedFetch, onSuccess, onClose, processing, setProcessing }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm the setup intent
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/account',
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError('Unable to update payment information. Please contact support@studyhall.xyz for assistance.');
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        // Attach payment method to subscription
        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/payment-method/attach`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentMethodId: setupIntent.payment_method,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update payment method');
        }

        onSuccess();
      } else {
        setError('Unable to update payment information. Please contact support@studyhall.xyz for assistance.');
        setProcessing(false);
      }
    } catch (err) {
      // Always show generic error message
      setError('Unable to update payment information. Please contact support@studyhall.xyz for assistance.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '1.5rem' }}>
        <PaymentElement />
      </div>
      
      {error && (
        <div style={{
          backgroundColor: '#ffe6e6',
          border: '1px solid #ff9999',
          color: '#cc0000',
          padding: '0.75rem',
          borderRadius: '6px',
          marginBottom: '1rem',
          fontSize: '14px'
        }}>
          Unable to update payment information. Please contact{' '}
          <a 
            href="mailto:support@studyhall.xyz" 
            style={{ color: '#cc0000', textDecoration: 'underline', fontWeight: 'bold' }}
          >
            support@studyhall.xyz
          </a>
          {' for assistance.'}
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        justifyContent: 'flex-end' 
      }}>
        <button
          type="button"
          onClick={onClose}
          disabled={processing}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: processing ? 'not-allowed' : 'pointer',
            opacity: processing ? 0.6 : 1,
            transition: 'background-color 0.2s'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: (!stripe || processing) ? 'not-allowed' : 'pointer',
            opacity: (!stripe || processing) ? 0.6 : 1,
            transition: 'background-color 0.2s'
          }}
        >
          {processing ? 'Updating...' : 'Update Payment Method'}
        </button>
      </div>
    </form>
  );
}

// Discount Options Component
function DiscountOptions({ 
  cancelReason, 
  selectedDiscount, 
  setSelectedDiscount, 
  applyingDiscount, 
  cancellingSubscription,
  navigate,
  onApplyDiscount,
  onProceedWithCancellation,
  onKeepSubscription
}) {
  // Get discount options based on cancellation reason
  const getDiscountOptions = () => {
    // For monthly subscriptions only
    if (cancelReason === 'freelance') {
      // "I am no longer looking for freelance work" -> 3 free months
      return [
        { value: 'free_3_months', label: '3 Free Months', description: 'Get 3 months completely free - no charge for the next 3 months' }
      ];
    } else {
      // Any other reason -> 50% off for 3 months
      return [
        { value: '50_3_months', label: '50% Off for 3 Months', description: 'Save 50% on your subscription for the next 3 months' }
      ];
    }
  };

  const discountOptions = getDiscountOptions();

  return (
    <>
      <h3 style={{ 
        color: '#38b44a', 
        marginBottom: '1rem',
        fontSize: '1.5rem',
        textAlign: 'center'
      }}>
        Before you go...
      </h3>
      <p style={{ 
        marginBottom: '1.5rem',
        textAlign: 'center',
        color: '#666'
      }}>
        We'd love to keep you! Here's a special offer just for you:
      </p>
      
      <div style={{ marginBottom: '2rem' }}>
        {discountOptions.map((option) => (
          <div
            key={option.value}
            onClick={() => setSelectedDiscount(option.value)}
            style={{
              border: selectedDiscount === option.value ? '2px solid #38b44a' : '2px solid #ddd',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              cursor: 'pointer',
              backgroundColor: selectedDiscount === option.value ? '#f0f9f0' : 'white',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '0.5rem' 
            }}>
              <input
                type="radio"
                checked={selectedDiscount === option.value}
                onChange={() => setSelectedDiscount(option.value)}
                style={{ marginRight: '0.75rem' }}
              />
              <strong style={{ fontSize: '1.1rem', color: '#333' }}>
                {option.label}
              </strong>
            </div>
            <p style={{ 
              margin: 0, 
              marginLeft: '1.75rem', 
              color: '#666', 
              fontSize: '0.9rem' 
            }}>
              {option.description}
            </p>
          </div>
        ))}
      </div>

      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={onKeepSubscription}
          disabled={applyingDiscount || cancellingSubscription}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: applyingDiscount || cancellingSubscription ? 'not-allowed' : 'pointer',
            opacity: applyingDiscount || cancellingSubscription ? 0.6 : 1,
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => {
            if (!applyingDiscount && !cancellingSubscription) {
              e.target.style.backgroundColor = '#5a6268';
            }
          }}
          onMouseOut={(e) => {
            if (!applyingDiscount && !cancellingSubscription) {
              e.target.style.backgroundColor = '#6c757d';
            }
          }}
        >
          Keep Subscription
        </button>
        <button
          type="button"
          onClick={onApplyDiscount}
          disabled={!selectedDiscount || applyingDiscount || cancellingSubscription}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: (!selectedDiscount || applyingDiscount || cancellingSubscription) ? 'not-allowed' : 'pointer',
            opacity: (!selectedDiscount || applyingDiscount || cancellingSubscription) ? 0.6 : 1,
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => {
            if (selectedDiscount && !applyingDiscount && !cancellingSubscription) {
              e.target.style.backgroundColor = '#2d8f3a';
            }
          }}
          onMouseOut={(e) => {
            if (selectedDiscount && !applyingDiscount && !cancellingSubscription) {
              e.target.style.backgroundColor = '#38b44a';
            }
          }}
        >
          {applyingDiscount ? 'Applying...' : 'Apply Discount'}
        </button>
        <button
          type="button"
          onClick={onProceedWithCancellation}
          disabled={applyingDiscount || cancellingSubscription}
          style={{
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: (applyingDiscount || cancellingSubscription) ? 'not-allowed' : 'pointer',
            opacity: (applyingDiscount || cancellingSubscription) ? 0.6 : 1,
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => {
            if (!applyingDiscount && !cancellingSubscription) {
              e.target.style.backgroundColor = '#c82333';
            }
          }}
          onMouseOut={(e) => {
            if (!applyingDiscount && !cancellingSubscription) {
              e.target.style.backgroundColor = '#dc3545';
            }
          }}
        >
          {cancellingSubscription ? 'Cancelling...' : 'Proceed with Cancellation'}
        </button>
      </div>
    </>
  );
}
